/* Two-level navigation.

   Ghost's navigation is flat, so hierarchy is encoded in the labels:
   a child reads "Parent › Child". The server renders every link in one list
   (see partials/navigation.hbs); this folds the children under their parent.

   Progressive enhancement: if this never runs, the flat list is still a
   complete, readable menu. */

const ISM_NAV_SEPARATOR = '›';

function ismNavigation() {
    const nav = document.querySelector('.gh-navigation-menu .nav');
    if (!nav) return;

    const items = Array.from(nav.children);
    if (!items.some(li => (li.dataset.navLabel || '').includes(ISM_NAV_SEPARATOR))) return;

    const groups = new Map();
    const ordered = [];

    items.forEach((li) => {
        const label = li.dataset.navLabel || '';
        const cut = label.indexOf(ISM_NAV_SEPARATOR);

        if (cut === -1) {
            ordered.push({ type: 'link', li });
            return;
        }

        const parent = label.slice(0, cut).trim();
        const child = label.slice(cut + ISM_NAV_SEPARATOR.length).trim();

        const link = li.querySelector('a');
        if (link) link.textContent = child;

        if (!groups.has(parent)) {
            const group = { type: 'group', parent, children: [] };
            groups.set(parent, group);
            ordered.push(group);
        }
        groups.get(parent).children.push(li);
    });

    if (!groups.size) return;

    const rebuilt = document.createDocumentFragment();

    ordered.forEach((entry) => {
        if (entry.type === 'link') {
            rebuilt.appendChild(entry.li);
            return;
        }

        const li = document.createElement('li');
        li.className = 'ism-nav-group';

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'ism-nav-toggle';
        toggle.textContent = entry.parent;
        toggle.setAttribute('aria-expanded', 'false');

        const submenu = document.createElement('ul');
        submenu.className = 'ism-nav-submenu';
        submenu.setAttribute('role', 'list');
        /* aria-expanded alone says a panel opened; aria-controls says which. */
        const panelId = 'ism-nav-panel-' + entry.parent.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        submenu.id = panelId;
        toggle.setAttribute('aria-controls', panelId);

        /* One column or five. Measured on the specialties group: twenty-four
           entries is 1001px of list inside a 560px panel, so nearly half of it
           sat behind a scrollbar the panel did not advertise. Past this many
           rows a dropdown stops being a menu and becomes a list with no end.

           The threshold is a LENGTH, not the group's name: a group the client
           grows past it gets columns by itself, and one they shrink goes back
           to a plain list. Eight is the largest a single column shows without
           scrolling in the shortest supported window. */
        const columns = entry.children.length > 8 ? ismPoleColumns(entry.children, panelId) : null;

        /* The class goes on AFTER the build, never before it. `ismPoleColumns`
           returns null when fewer than two poles matched — and a first draft had
           already put `is-mega` on the panel by then, so twenty-four bare <li>
           would have been laid out as cells of a five-column grid. The class has
           to describe what was actually built. */
        if (columns) {
            submenu.classList.add('is-mega');
            li.classList.add('has-mega');
            columns.forEach(col => submenu.appendChild(col));
        } else {
            entry.children.forEach(child => submenu.appendChild(child));
        }

        // A child marked current means the whole group is the current section.
        if (entry.children.some(child => child.classList.contains('nav-current'))) {
            li.classList.add('nav-current');
        }

        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = toggle.getAttribute('aria-expanded') === 'true';
            closeAll();
            toggle.setAttribute('aria-expanded', String(!open));
        });

        li.append(toggle, submenu);
        rebuilt.appendChild(li);
    });

    nav.replaceChildren(rebuilt);
    nav.classList.add('is-grouped');

    /* Source starts its nav items at opacity 0 and reveals them by putting
       `is-dropdown-loaded` on the header — a class its own dropdown() adds.
       Grouping replaces that dropdown, so without this the whole menu stays
       invisible while measuring a full 720px wide. */
    document.querySelector('.gh-navigation')?.classList.add('is-dropdown-loaded');

    function closeAll() {
        nav.querySelectorAll('.ism-nav-toggle[aria-expanded="true"]')
           .forEach(t => t.setAttribute('aria-expanded', 'false'));
    }

    /* A click OUTSIDE the group closes it; one inside it does not.

       The first version closed on any document click, which made the panel's own
       empty space a trapdoor: a visitor aiming at a row and landing 5px above it
       lost the whole menu, and had to reopen it and aim again. That reads as
       "the link did nothing", and it is the reason the panel's dead area was
       worth measuring at all (see the note on `align-items: stretch` in ism.css).

       A click that DOES hit a row still leaves the panel open, and that is
       correct: the page is navigating away. */
    document.addEventListener('click', (e) => {
        if (e.target.closest?.('.ism-nav-group')) return;
        closeAll();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll();
    });
}

/* The home page renders the whole navigation into a grid and names the group
   it wants. Keep that group's children, drop everything else, and strip the
   parent prefix — the same "Parent › Child" convention the header folds on,
   read from the one place that knows it. */
function ismNavGroups() {
    document.querySelectorAll('[data-ism-nav-group]').forEach((list) => {
        const wanted = (list.dataset.ismNavGroup || '').trim().toLowerCase();
        if (!wanted) return;

        list.querySelectorAll('li').forEach((li) => {
            const a = li.querySelector('a');
            const label = a ? a.textContent : '';
            const cut = label.indexOf(ISM_NAV_SEPARATOR);

            if (cut === -1) { li.remove(); return; }
            if (label.slice(0, cut).trim().toLowerCase() !== wanted) { li.remove(); return; }

            a.textContent = label.slice(cut + ISM_NAV_SEPARATOR.length).trim();

            /* Icons live in a sprite the template renders once. The grid only
               knows an entry by its URL, so the slug comes from there; an
               unknown one falls back rather than leaving a gap. */
            if (list.dataset.ismIcons !== 'off') {
                const slug = (a.getAttribute('href') || '').replace(/^\/|\/$/g, '').split('/').pop();
                const id = document.getElementById('ism-i-' + slug) ? slug : 'default';
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('class', 'ism-spec-icon');
                svg.setAttribute('aria-hidden', 'true');
                svg.setAttribute('viewBox', '0 0 24 24');
                const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
                use.setAttribute('href', '#ism-i-' + id);
                svg.appendChild(use);
                a.prepend(svg);
            }
        });

        /* Nothing matched: hide the section rather than leave an empty heading. */
        if (!list.children.length) { list.closest('section')?.remove(); return; }

        /* The figure above the band, corrected from the menu that is actually
           live. The template renders the count at the time of writing so the
           page is never blank or zero without scripts; this is what keeps it
           true after the client adds a specialty. Written before the counter
           animation runs — main.js calls this first — so the count-up ends on
           the corrected number rather than the authored one. */
        document.querySelectorAll('[data-ism-spec-count]')
            .forEach(el => { el.textContent = list.children.length; });

        if (list.hasAttribute('data-ism-nav-poles')) ismGroupIntoPoles(list);
    });
}

/* The specialties belong to poles, and saying so is the whole point of the
   section: twenty-two equal boxes tell a patient nothing. The map is by SLUG,
   never by label — the labels are the client's to edit in the Ghost menu, the
   slugs carry the SEO and do not move. An entry whose slug is not listed lands
   in the last group instead of vanishing, so adding a specialty to the menu can
   never silently drop it from the home. */
const ISM_POLES = [
    ['Syndrome d\'Ehlers-Danlos', [
        'syndrome-ehlers-danlos', 'vivre-avec-le-sed',
        'temoignages-patients-sed', 'interviews-professionnels-sed',
    ]],
    ['Cœur et vaisseaux', [
        'cardiologie', 'examens-cardiologie', 'cardiologie-du-sport',
        'la-rythmologie', 'angiologie-medecine-vasculaire',
        'echographie-doppler-vasculaire-veineux',
    ]],
    ['Souffle, sommeil et milieux extrêmes', [
        'pneumologie', 'medecine-du-sommeil',
        'medecine-de-plongee', 'medecine-aerospatiale',
    ]],
    ['Médecine générale et suivi', [
        'la-medecine-generale', 'la-pediatrie', 'gynecologie',
        'la-fertilite', 'la-denutrition', 'infectiologie',
    ]],
    ['Neurologie, rééducation et sport', [
        'la-neurologie', 'la-medecine-mpr', 'medecine-du-sport',
        'la-psychiatrie-psychologie',
    ]],
    ['Autres spécialités', []],
];

/* The one place a specialty is assigned to a pole. Both the home band and the
   header panel call this, so the menu and the page cannot disagree about which
   pole a specialty is in — and adding a slug to ISM_POLES fixes both at once.
   Returns a Map keyed by pole name, every pole present including the empty
   ones; dropping those is each caller's job, because each renders a different
   thing for them. An entry whose slug is in no pole lands in the last one
   rather than vanishing. */
function ismBucketPoles(items) {
    const slugOf = (li) => {
        const href = li.querySelector('a')?.getAttribute('href') || '';
        return href.replace(/^\/|\/$/g, '').split('/').pop();
    };

    const buckets = new Map(ISM_POLES.map(([name]) => [name, []]));
    const fallback = ISM_POLES[ISM_POLES.length - 1][0];

    items.forEach((li) => {
        const hit = ISM_POLES.find(([, slugs]) => slugs.includes(slugOf(li)));
        buckets.get(hit ? hit[0] : fallback).push(li);
    });

    return buckets;
}

/* The header panel: one <li> per pole, each holding its own heading and list.
   A heading inside a list item is valid, and it is what makes this readable in
   the burger column too, where the grid does not apply and the panel is a plain
   indented list.

   `i === 0` for the lead, never a label match — same reason as the home band:
   the client renames poles in Ghost admin and a rename must not be able to move
   the emphasis to another specialty. */
function ismPoleColumns(items, panelId) {
    const buckets = ismBucketPoles(items);
    const columns = [];

    ISM_POLES.forEach(([name], i) => {
        const rows = buckets.get(name);
        if (!rows.length) return;

        const col = document.createElement('li');
        col.className = i === 0 ? 'ism-nav-col is-lead' : 'ism-nav-col';

        /* A <p>, not an <h3>, and the list points at it with `aria-labelledby`.

           The panel lives in <header>, so a heading here precedes the page's own
           <h1> in the document — a reader navigating by heading would meet
           "Syndrome d'Ehlers-Danlos" at level 3 before the page said what page it
           is. `aria-labelledby` gives each list the same name a heading would
           have given it, and leaves the outline to the document. */
        const titleId = panelId + '-pole-' + i;
        const title = document.createElement('p');
        title.className = 'ism-nav-col-title';
        title.id = titleId;
        title.append(document.createTextNode(name));

        const ul = document.createElement('ul');
        ul.setAttribute('role', 'list');
        ul.setAttribute('aria-labelledby', titleId);
        rows.forEach(li => ul.appendChild(li));

        col.append(title, ul);
        columns.push(col);
    });

    /* Fewer than two poles matched: decline, so the caller renders the plain
       list instead of one column called "Autres spécialités" holding everything.

       NULL, not the input array. A first version signalled the decline by
       handing `items` back, and the caller tested the result for truthiness — a
       non-empty array is truthy, so `is-mega` went on anyway and the flat list
       was laid out as cells of a five-column grid. The decline has to be a value
       the caller cannot mistake for a result. Proved by executing the decline
       path with ten unmatched slugs, not by reading it. */
    return columns.length > 1 ? columns : null;
}

function ismGroupIntoPoles(list) {
    const buckets = ismBucketPoles([...list.children]);

    const wrap = document.createElement('div');
    wrap.className = 'ism-poles';

    /* Two tiers, not five equal ones. Rendered as five equal columns this block
       was a quarter of the home page spent on a directory where every entry
       looked exactly as important as every other — including the one the centre
       exists for. The first pole becomes the band's only surface; the rest
       become a ruled index with no surface at all.

       `i === 0`, an index into ISM_POLES, and never a label match: the client
       renames poles in Ghost, and a rename must not be able to hand the lead to
       another specialty. If the SED bucket is ever empty, `items.length` returns
       first and nothing is promoted — the band degrades to a plain index rather
       than crowning whatever happens to be next. */
    const index = document.createElement('div');
    index.className = 'ism-poles-index';

    ISM_POLES.forEach(([name], i) => {
        const items = buckets.get(name);
        if (!items.length) return;              /* no empty heading */

        const isLead = i === 0;

        const pole = document.createElement('section');
        pole.className = isLead ? 'ism-pole is-lead' : 'ism-pole';

        const title = document.createElement('h3');
        title.className = 'ism-pole-title';
        title.textContent = name;

        /* Counted here, never authored: a menu edit updates it by itself. Only
           the index carries one — on the lead the rows are the point.

           `aria-hidden`, because a bare integer inside the heading is read as
           part of it: "Médecine générale et suivi 6". The number is a scanning
           aid for a sighted reader and pure noise to a screen reader, which
           already announces "list, 6 items" when it reaches the list below. */
        if (!isLead) {
            const count = document.createElement('span');
            count.className = 'ism-pole-count';
            count.setAttribute('aria-hidden', 'true');
            count.textContent = items.length;
            title.appendChild(count);
        }

        const ul = document.createElement('ul');
        ul.className = 'ism-pole-list';
        items.forEach((li) => ul.appendChild(li));

        pole.append(title, ul);
        (isLead ? wrap : index).appendChild(pole);
    });

    /* Only if it has children — with a single pole in the menu the index would
       otherwise render as an empty element with its own column rules. */
    if (index.children.length) wrap.appendChild(index);

    list.replaceWith(wrap);
}

